import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, AlertCircleIcon, MailIcon, MessageCircleIcon, ClockIcon } from 'lucide-react';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
// Formspree endpoint — shared with the site contact form.
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const highlights = [{
  icon: MessageCircleIcon,
  title: 'Product questions',
  text: 'Not sure if RD Ready fits your exam? Ask away.'
}, {
  icon: MailIcon,
  title: 'Account & access',
  text: 'Help with study access, sign-in, or your membership.'
}, {
  icon: ClockIcon,
  title: 'Quick replies',
  text: 'We aim to respond to every message within 48 hours.'
}];
export function RDReadyContact() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (formData.get('_gotcha')) {
      setStatus('success');
      return;
    }
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data?.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };
  const inputClass = 'w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] transition-colors focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20';
  const labelClass = 'mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#475569]';
  return <div className="w-full overflow-hidden bg-white text-[#1E293B]" style={{
    fontFamily: '"Poppins", "Inter", system-ui, sans-serif'
  }} data-id="element-2585" data-mp="2k853bdo9fyy6">
      {/* HERO */}
      <section className="px-5 pt-14 pb-8 text-center md:px-8 md:pt-20" data-id="element-2586" data-mp="2aa8uwtmpji3r">
        <motion.div initial="hidden" animate="visible" variants={{
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} className="mx-auto max-w-3xl" data-id="element-2587" data-mp="269f5twxj3x9a">
          <motion.p variants={fadeUp} transition={{
          duration: 0.5
        }} className="text-xs font-bold uppercase tracking-[0.22em]" style={{
          color: TEAL
        }} data-id="element-2588" data-mp="uwcnawbvcxmh">
            Contact
          </motion.p>
          <motion.h1 variants={fadeUp} transition={{
          duration: 0.5
        }} className="mt-4 text-4xl font-extrabold leading-[1.1] tracking-tight text-[#0F172A] sm:text-5xl" data-id="element-2589" data-mp="3fggrm700nce1">
            Let&apos;s help you get{' '}
            <span style={{
            color: BLUE
          }} data-id="element-2590" data-mp="3u9ajyt0dzn04">
              exam ready.
            </span>
          </motion.h1>
          <motion.p variants={fadeUp} transition={{
          duration: 0.5
        }} className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#5A6B7B]" data-id="element-2591" data-mp="71f4qxfq1okm">
            Have a question about RD Ready, your study access, or which exam to
            prepare for? Send us a note and we&apos;ll get back to you.
          </motion.p>
        </motion.div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="px-5 py-8 md:px-8" data-id="element-2592" data-mp="1nhug1us2gmt8">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3" data-id="element-2593" data-mp="1ngje80i6lp4r">
          {highlights.map(({
          icon: Icon,
          title,
          text
        }) => <div key={title} className="rounded-2xl bg-[#F7F9FC] p-6 text-center" data-id="element-2594" data-mp="1igoklp70ulg0">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full" style={{
            backgroundColor: '#EEF4FF'
          }} data-id="element-2595" data-mp="puc98phyturv">
                <Icon className="h-5 w-5" style={{
              color: BLUE
            }} data-id="element-2596" data-mp="zs04rhneb6b3" />
              </span>
              <h3 className="mt-4 text-sm font-bold text-[#0F172A]" data-id="element-2597" data-mp="73uj8soipda7">{title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-[#5A6B7B]" data-id="element-2598" data-mp="71rv6e6xdu84">
                {text}
              </p>
            </div>)}
        </div>
      </section>

      {/* FORM */}
      <section className="px-5 py-10 md:px-8 md:py-14" data-id="element-2599" data-mp="2njr4aloz74ax">
        <div className="mx-auto max-w-3xl rounded-3xl bg-[#F7F9FC] p-6 md:p-10" data-id="element-2600" data-mp="28ttczwvr1yhi">
          {status === 'success' ? <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} className="rounded-2xl bg-white p-8 text-center shadow-[0_2px_16px_rgba(15,23,42,.05)]" data-id="element-2601" data-mp="2dsecfu36ge4w">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{
            backgroundColor: '#E1EEE8'
          }} data-id="element-2602" data-mp="2sj3rj7jrede1">
                <CheckCircle2Icon className="h-6 w-6" style={{
              color: TEAL
            }} data-id="element-2603" data-mp="1l2xrtilsu1j8" />
              </span>
              <h2 className="mt-5 text-2xl font-bold text-[#0F172A]" data-id="element-2604" data-mp="3iqrqdjmxpi1s">
                Message sent!
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2605" data-mp="3dzjgkmhozuo3">
                Thanks for reaching out — we&apos;ll get back to you within 48
                hours. Keep an eye on your inbox (and spam folder, just in
                case).
              </p>
              <button type="button" onClick={() => setStatus('idle')} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold transition-transform hover:translate-x-0.5" style={{
            color: BLUE
          }} data-id="element-2606" data-mp="300junmlkdtvm">
                Send another message <ArrowRightIcon className="h-4 w-4" data-id="element-2607" data-mp="t8iyqbvdb55c" />
              </button>
            </motion.div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2608" data-mp="obitg36ce6fa">
              <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px'
          }} data-id="element-2609" data-mp="wqowxvwrtvzi" />

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-2610" data-mp="dtdw46vhquhy">
                <div data-id="element-2611" data-mp="duyu66h0kvkk">
                  <label htmlFor="name" className={labelClass} data-id="element-2612" data-mp="huha0ntzaggj">
                    Full name *
                  </label>
                  <input id="name" name="name" type="text" required className={inputClass} placeholder="Your name" data-id="element-2613" data-mp="mtarssgastmi" />
                </div>
                <div data-id="element-2614" data-mp="237tz4q763p9j">
                  <label htmlFor="email" className={labelClass} data-id="element-2615" data-mp="2370ym6ysxvzu">
                    Email *
                  </label>
                  <input id="email" name="email" type="email" required className={inputClass} placeholder="you@example.com" data-id="element-2616" data-mp="2ocnx07kyme5k" />
                </div>
              </div>

              <div data-id="element-2617" data-mp="2ta603hcapz5n">
                <label htmlFor="topic" className={labelClass} data-id="element-2618" data-mp="2t8utl9rgudau">
                  What can we help with? *
                </label>
                <select id="topic" name="topic" required defaultValue="" className={inputClass} data-id="element-2619" data-mp="3310w3dfcgd4a">
                  <option value="" disabled data-id="element-2620" data-mp="3c1p0rhiul14i">
                    Select a topic…
                  </option>
                  <option value="RD Exam prep" data-id="element-2621" data-mp="1mlzbr7dleomn">RD Exam prep</option>
                  <option value="CNSC Exam prep" data-id="element-2622" data-mp="1moc1i6ple3a9">CNSC Exam prep</option>
                  <option value="Study access / account" data-id="element-2623" data-mp="1hoz7n20azvy6">
                    Study access / account
                  </option>
                  <option value="Billing" data-id="element-2624" data-mp="3td40yh5vdb76">Billing</option>
                  <option value="General question" data-id="element-2625" data-mp="235bu1e6og8i">General question</option>
                </select>
              </div>

              <div data-id="element-2626" data-mp="3ki7isz468o76">
                <label htmlFor="message" className={labelClass} data-id="element-2627" data-mp="3kgmuvscd43ia">
                  Message *
                </label>
                <textarea id="message" name="message" required rows={6} placeholder="Tell us how we can help…" className={`${inputClass} resize-none`} data-id="element-2628" data-mp="3akjk5y3g2wjq" />
              </div>

              {status === 'error' && <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4" data-id="element-2629" data-mp="35lykq0w0ogwc">
                  <AlertCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" data-id="element-2630" data-mp="zt9zxy0z925a" />
                  <p className="text-sm text-red-800" data-id="element-2631" data-mp="13uc0ci6mrk8u">{errorMessage}</p>
                </div>}

              <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" style={{
            backgroundColor: BLUE
          }} data-id="element-2632" data-mp="2bt6x4dk2fhog">
                {status === 'submitting' ? <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-2633" data-mp="2zij7vhv1jxsv" />
                    Sending…
                  </> : <>
                    Send message <ArrowRightIcon className="h-4 w-4" data-id="element-2634" data-mp="2pmya5vx09jox" />
                  </>}
              </button>

              <p className="pt-1 text-xs text-[#94A3B8]" data-id="element-2635" data-mp="1e5q7pg5x6dne">
                Your information stays private. We respond personally to every
                message.
              </p>
            </form>}
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-2636" data-mp="11nm7bxpalk35">
          Prefer to browse first?{' '}
          <Link to="/cnsc-prep" className="font-semibold" style={{
          color: BLUE
        }} data-id="element-2637" data-mp="1gjv3urhstjbv">
            Explore RD Ready study tools
          </Link>
          .
        </p>
      </section>
    </div>;
}